function logDebug(module, isDebugging, msg, ...params) {
    if (isDebugging) {
        console.debug(`${module} :: ${msg}`, ...params);
    }
}

function debugCallback(isDebugging, callback) {
    if (isDebugging) {
        callback();
    }
}

function mkFontInfo(sizeInPixels, family) {
    const fontInfo = {
        sizeInPixels,
        font: `${sizeInPixels}px ${family}`
    };
    return fontInfo;
}

function isInitialized(x) {
    return typeof x !== 'undefined';
}

function isNullOrUndefined(x) {
    return !isInitialized(x) || x === null;
}

/**
 * Clamps a value between a min and max.
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

/**
 * Ignore the passed value.
 * @param _
 * @return {void}
 */
function ignore(_) { }

/**
 * Gets the name of the discriminated union.
 * If there is a nested DU, get the name of the first level down.
 * e.g "CommonInteractionMode Standard" returns "Standard"
 * @param du
 * @return {*}
 */
function getDUName(du) {
    if (du?.fields && du.fields.length === 1) {
        return du.fields[0].name;
    } else {
        return du?.name ?? du;
    }
}

/**
 * Gets the top-level name of the discriminated union.
 * If there is a nested DU, it is ignored.
 * e.g "CommonInteractionMode Standard" returns "CommonInteractionMode"
 * @param du
 * @return {*}
 */
function getTopLevelDUName(du) {
    return du?.name ?? du;
}

const displayMapTypes = Object.freeze({
    ANATOMY: "Anatomy",
    LOCAL_EXPANSION_GROWTH: "LocalExpansionGrowth",
    DIAMETRIC_GROWTH: "DiametricGrowth",
    ILT: "Ilt",
    SECTIONAL_RAW: "SectionalRaw",
    CONTINUOUS_RAW: "ContinuousRaw",
    STRAIN: "Strain",
    VIGR: "Vigr"
});

export const ViewportIdentifier = Object.freeze({
    MEASUREMENT: "Measurement",
    STENT_PLANNING: "StentPlanning"
});

const constants = Object.freeze({
    staticLabelCanvasId: "staticLabelCanvas",
    worldLabelCanvasId: "worldLabelCanvas",
    anatomyScalarBarCanvasId: "anatomyScalarBarCanvas",
    planeResolution: 21,
    screenshotSize: {
        width: 600,
        height: 400
    },
    splitScreenshotSize: {
        width: 350,
        height: 400,
    },
    splitScreenshotPadding: 2,
    meshOpacity: 0.5,
    stentPlanningWallOpacity: 0.15,
    stentPlanningAnatomyOpacity: 0.10,
    dataArrayNames: {
        analysisPipelineVersion: "AP_Version",
        analysisRevision: "Analysis_Revision",
        acquisitionDate: "Acquisition_Date",
        renalColor: "Colour",
        renalOpacity: "Opacity",
        anatomyColor: "Anatomy_Colour",
        landmarkIds: "Landmark_IDs",
        landmarkLengths: "Landmark_Lengths",
        landmarkAngles: "Landmark_Angles",
        cArmAngles: "C_Arm_Angles",
        cArmAngleNames: "C_Arm_Angle_Names",
    }
});

export const allCanvases = [
    "measurement_" + constants.staticLabelCanvasId,
    "measurement_" + constants.worldLabelCanvasId,
    "static_" + constants.staticLabelCanvasId,
    "static_" + constants.worldLabelCanvasId,
    constants.anatomyScalarBarCanvasId,
    `pickedMapValue_${constants.staticLabelCanvasId}`,
    `pickedMapValue_${constants.worldLabelCanvasId}`];

export const DicomView = Object.freeze({
    AXIAL: 'Axial',
    SAGITTAL: 'Sagittal',
    CORONAL: 'Coronal',
    CENTERLINE: 'Centerline'
});

// Corresponds to the Widget DU in VisualizationBindings.fs.
export const WidgetType = Object.freeze({
    DISTANCE_WIDGET: 'DistanceWidget',
    SEGMENTATION_WIDGET: 'SegmentationWidget',
    CROSSHAIR_WIDGET: 'CrosshairWidget',
    ANGLE_WIDGET: 'AngleWidget'
});

// Corresponds to the F# SlicingMode discriminated union.
export const SlicingModeName = Object.freeze({
    AXIAL: 'Axial',
    SAGITTAL: 'Sagittal',
    CORONAL: 'Coronal'
});

// Corresponds to the F# LengthMeasurementState discriminated union.
export const LengthMeasurementStateName = Object.freeze({
    IDLE: 'Idle',
    ADDING: 'Adding',
    REMOVING: 'Removing'
});

// Corresponds to the F# AngleMeasurementState discriminated union.
export const AngleMeasurementStateName = Object.freeze({
    IDLE: 'Idle',
    ADDING: 'Adding',
    REMOVING: 'Removing'
});

// Corresponds to the F# CprViewMode discriminated union.
export const CprViewMode = Object.freeze({
    STRETCHED: 'Stretched',
    STRAIGHTENED: 'Straightened'
});

export const CprControlPointMode = Object.freeze({
    EDIT: 'Edit',
    ADD_OR_DELETE: 'AddOrDelete'
});

export { logDebug, debugCallback, mkFontInfo, isInitialized, isNullOrUndefined, constants, ignore, getDUName, getTopLevelDUName, displayMapTypes, clamp };
